import {
  Box,
  Button,
  Card,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../API/api';
import { ConfigContext } from '../../App/App';
import { AppContext } from '../../App/AppContext';
import styles from './DynamicForm.module.css';
import DynamicFormField from './DynamicFormFields/DynamicFormField';

/* ---------- types ---------- */
// Each group can be triggered by one or more values
export interface ChildGroup {
  show_on_value: string | string[];
  fields: FormField[];
}

// The main field interface
export interface FormField {
  Label: string;
  Description: string;
  DataType: string;
  AllowedValues: string[];
  Required: boolean;
  FieldId: string;
  Validation: string;
  // We can handle both new and old styles:
  // "Children" (array) or single "children" (object) by converting in parse step.
  Children?: ChildGroup[];
}

/* ---------- helpers ---------- */
const isEmpty = (v: any) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

/**
 * For each top-level field in "EntranceForm" => form_details => properties,
 * we transform it into our FormField + ChildGroup[] structure.
 *
 * This function now handles both:
 *  - Old style (fieldDef.children + fieldDef.children_show_on_value)
 *  - New style (fieldDef.Children => array of groups)
 */
function parseFormFieldsFromConfig(config: any): FormField[] {
  const props = config?.EntranceForm?.form_details?.properties;
  if (!props || Object.keys(props).length === 0) return [];

  return Object.entries(props).map(([_, fieldDef]: [string, any]) => {
    // Basic metadata
    const meta = fieldDef.metadata?.[0] ?? {};

    // Step 1: If there's an array "Children", parse that as new style
    let parsedChildren: ChildGroup[] | undefined;

    if (Array.isArray(fieldDef.Children)) {
      // NEW style: "Children": [ { show_on_value: ..., fields: {...} } ]
      parsedChildren = fieldDef.Children.map((grp: any) => {
        const rawFields = grp.fields || {};
        const childFieldArray: FormField[] = Object.entries(rawFields).map(
          ([__, chDef]: [string, any]) => {
            const meta2 = chDef.metadata?.[0] ?? {};
            return {
              Label: chDef.title,
              Description: chDef.description,
              DataType: meta2.data_type,
              Required: meta2.required,
              AllowedValues: meta2.allowed_values,
              Validation: meta2.validation,
              FieldId: meta2.field_id,
              Children: [], // deeper nesting if needed
            };
          }
        );

        return {
          show_on_value: grp.show_on_value,
          fields: childFieldArray,
        };
      });
    }
    // Step 2: Otherwise, check if there's an old-style "children" (object)
    else if (fieldDef.children) {
      // OLD style: single "children" object + single "children_show_on_value"
      const triggerValue = fieldDef.children_show_on_value ?? '';
      const rawChildObj = fieldDef.children; // { "Child Field": {...}, ... }

      // Convert each child in the object to a FormField
      const childFieldArray: FormField[] = Object.entries(rawChildObj).map(
        ([__, chDef]: [string, any]) => {
          const meta2 = chDef.metadata?.[0] ?? {};
          return {
            Label: chDef.title,
            Description: chDef.description,
            DataType: meta2.data_type,
            Required: meta2.required,
            AllowedValues: meta2.allowed_values,
            Validation: meta2.validation,
            FieldId: meta2.field_id,
            Children: [],
          };
        }
      );

      // Wrap them into a single group
      parsedChildren = [
        {
          show_on_value: triggerValue,
          fields: childFieldArray,
        },
      ];
    }
    // Step 3: No children found in either style => undefined or empty
    else {
      parsedChildren = undefined;
    }

    // Build the parent FormField
    return {
      Label: fieldDef.title,
      Description: fieldDef.description,
      DataType: meta.data_type,
      Required: meta.required,
      AllowedValues: meta.allowed_values,
      Validation: meta.validation,
      FieldId: meta.field_id,
      Children: parsedChildren,
    };
  });
}

function DynamicForm() {
  const config = useContext(ConfigContext);
  const { dispatch, state } = useContext(AppContext);
  const navigate = useNavigate();

  /* ----------- derive form fields from config ----------- */
  const formFields: FormField[] = useMemo(() => {
    return parseFormFieldsFromConfig(config);
  }, [config]);

  /**
   * We'll store an array of records describing each field's validation status:
   * {
   *    FieldName: string,
   *    FieldId: string,
   *    IsValid: boolean,
   *    Error: string,
   *    FieldValue: any
   * }
   */
  const buildInitialValidations = useCallback(() => {
    // Flatten top-level fields plus each group's children
    return formFields.flatMap((parent) => {
      const parentRec = {
        FieldName: parent.Label,
        FieldId: parent.FieldId,
        IsValid: !parent.Required, // If required, start invalid until user sets a value
        Error: '',
        FieldValue: '',
      };

      // For each group => for each child in that group
      const childRecs = parent.Children
        ? parent.Children.flatMap((grp) =>
            grp.fields.map((child) => ({
              FieldName: child.Label,
              FieldId: child.FieldId,
              IsValid: !child.Required,
              Error: '',
              FieldValue: '',
            }))
          )
        : [];

      return [parentRec, ...childRecs];
    });
  }, [formFields]);

  const [fieldValidations, setFieldValidations] = useState<any[]>(buildInitialValidations);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  if(fieldValues){
    console.log("fieldValues", fieldValues)
  }

  // The overall form validity
  const [formValid, setFormValid] = useState(() => fieldValidations.every((v) => v.IsValid));

  // Rebuild skeleton if formFields change
  useEffect(() => {
    const initial = buildInitialValidations();
    setFieldValidations(initial);
    setFormValid(initial.every((v) => v.IsValid));
  }, [formFields, buildInitialValidations]);

  /* ---------- stable validation callback ---------- */
  const validateField = useCallback(
    (fieldName: string, isValid: boolean, error: string, fieldValue: any) => {
      setFieldValues((currentValues) => {
        const newFieldValues = { ...currentValues, [fieldName]: fieldValue };

        setFieldValidations((prevValidations) => {
          let next = [...prevValidations];

          // 1) Find the top-level parent or child field in formFields
          // We'll search a bit.
          const findParent = (lbl: string) => formFields.find((p) => p.Label === lbl);

          // We search child fields in all groups
          const findChild = (lbl: string) => {
            for (const parent of formFields) {
              if (!parent.Children) continue;
              for (const grp of parent.Children) {
                const childField = grp.fields.find((c) => c.Label === lbl);
                if (childField) {
                  return { parent, group: grp, childField };
                }
              }
            }
            return null;
          };

          // Attempt to locate which we are updating
          const parentMeta = findParent(fieldName);
          const childMeta = findChild(fieldName);

          // 2) If it's a child, we check if the parent's current selection
          // triggers that child's group
          if (childMeta) {
            const { parent, group, childField } = childMeta;
            const parentValue = newFieldValues[parent.Label] || '';

            // Check if parent's value is in group.show_on_value
            // (which can be a string or an array of strings)
            const triggered = Array.isArray(group.show_on_value)
              ? group.show_on_value.includes(parentValue)
              : group.show_on_value === parentValue;

            if (!triggered) {
              // If not triggered, remove from validations
              next = next.filter((v) => v.FieldId !== childField.FieldId);
            } else {
              // If triggered, ensure a record for this child is present & updated
              const idx = next.findIndex((v) => v.FieldId === childField.FieldId);
              if (idx === -1) {
                // Insert
                next.push({
                  FieldName: childField.Label,
                  FieldId: childField.FieldId,
                  IsValid: isValid,
                  Error: error,
                  FieldValue: fieldValue,
                });
              } else {
                // Update
                next[idx] = {
                  ...next[idx],
                  IsValid: isValid,
                  Error: error,
                  FieldValue: fieldValue,
                };
              }
            }
          }
          // 3) If it's a parent field
          else if (parentMeta) {
            // Update or insert the parent record
            const idx = next.findIndex((v) => v.FieldId === parentMeta.FieldId);
            const updatedRec = {
              FieldName: fieldName,
              FieldId: parentMeta.FieldId,
              IsValid: isValid,
              Error: error,
              FieldValue: fieldValue,
            };
            if (idx === -1) {
              next.push(updatedRec);
            } else {
              next[idx] = { ...next[idx], ...updatedRec };
            }

            // 4) Now handle its child groups if any
            if (parentMeta.Children) {
              // For each group, does the parent's new value match show_on_value?
              parentMeta.Children.forEach((grp) => {
                const triggered = Array.isArray(grp.show_on_value)
                  ? grp.show_on_value.includes(fieldValue)
                  : grp.show_on_value === fieldValue;

                if (triggered) {
                  // Ensure those child fields are added
                  grp.fields.forEach((child) => {
                    const cIdx = next.findIndex((v) => v.FieldId === child.FieldId);
                    if (cIdx === -1) {
                      next.push({
                        FieldName: child.Label,
                        FieldId: child.FieldId,
                        IsValid: !child.Required && !isEmpty(fieldValue),
                        Error: '',
                        FieldValue: newFieldValues[child.Label] ?? '',
                      });
                    }
                  });
                } else {
                  // Remove them if they exist
                  grp.fields.forEach((child) => {
                    next = next.filter((v) => v.FieldId !== child.FieldId);
                  });
                }
              });
            }
          }

          // 5) If required & empty => force invalid
          if ((parentMeta?.Required || childMeta?.childField?.Required) && isEmpty(fieldValue)) {
            isValid = false;
            error = ''; // or "This field is required." if you prefer
          }

          // 6) Re-check overall form validity
          const allValid = next.every((v) => v.IsValid);
          setFormValid(allValid);

          return next;
        });

        return newFieldValues;
      });
    },
    [formFields]
  );

  /* ---------- submit ---------- */
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const afterSubmit = () => {
    dispatch({ type: 'setEnteredInformation', hasEnteredInformation: true });
    navigate('/Home');
  };

  const submitForm = async () => {
    if (!formValid) return;
    setSubmitting(true);

    const request = {
      indexname: config.IndexName,
      sessionid: state.sessionid,
      objectid:
        config?.EntranceForm?.form_details?.object_id ?? 'missing-object-id',
      fields: fieldValidations.map((v) => ({
        fieldid: v.FieldId,
        fieldvalue: v.FieldValue,
      })),
    };

    try {
      await api.submitDyanmicInformation.submitDyanmicInformation(request);
      afterSubmit();
    } catch (err) {
      console.error(err);
      setSnackOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- auto-skip if no fields ---------- */
  useEffect(() => {
    if (formFields.length === 0) {
      dispatch({ type: 'setEnteredInformation', hasEnteredInformation: true });
      navigate('/Home', { replace: true });
    }
  }, [formFields.length, dispatch, navigate]);

  if (formFields.length === 0) return null;

  return (
    <Box component="main" id="main" sx={{ flex: 1 }}>
      <Card
        sx={{
          p: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          justifyContent: 'space-between',
        }}
      >
        {submitting && (
          <Card
            role="status"
            aria-live="polite"
            sx={{
              background: 'white',
              opacity: 0.5,
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 99,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Card>
        )}

        <form className="welcomeSubmit" aria-busy={submitting}>
          <Box sx={{ mb: 2 }}>
            <Typography
              component="h1"
              sx={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center' }}
            >
              Welcome!
            </Typography>
            <Typography sx={{ textAlign: 'center' }}>
              Enter your information to get started.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Render each top-level parent field */}
            {formFields.map((ff) => (
              <DynamicFormField
                key={ff.FieldId}
                form_field={ff}
                validateField={validateField}
                isChild={false}
              />
            ))}
          </Box>

          <Button
            variant="outlined"
            type="button"
            onClick={submitForm}
            disabled={!formValid || submitting}
            sx={{
              width: '100%',
              mt: 2,
              background: formValid
                ? `${config.PrimaryColor} !important`
                : '#e8e8e8 !important',
              color: formValid ? 'white !important' : 'gray !important',
              border: '0 !important',
            }}
            className={styles.submitDynamicFormButton}
          >
            Get&nbsp;Started
          </Button>
        </form>
      </Card>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message="Something went wrong. Please try again."
      />
    </Box>
  );
}

export default DynamicForm;
